import { Component, createRef } from 'react';
import { withTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import moment from 'moment';

// The Search Console property list has one implementation in the app, on the tool that introduced
// it; every GSC tool picks a property out of everything Search Console exposes to the user, which
// is a wider list than the domains linked to GDDash.
import { getUserDomains } from '../../DomainPerformance/query';
import ScopeControls from '../GscTools/components/ScopeControls';
import CoverageStrip from '../GscTools/components/CoverageStrip';
import { ELIGIBLE_PERMISSIONS, probeLatestDate } from '../GscTools/query';
import { downloadCsv } from '../GscTools/csv';
import {
    buildSeries,
    coverageFor,
    metricsFor,
    normalizeTotals,
    reportsPosition,
    rowMetrics,
    sumRows,
} from '../GscTools/metrics';
import {
    DETAIL_MAX_DAYS,
    defaultPeriod,
    detailPeriod,
    formatPeriod,
    periodEndingOn,
} from '../GscTools/period';
import { readParams, writeParams } from '../GscTools/url';

import BrandCoverage from './components/BrandCoverage';
import BrandTerms from './components/BrandTerms';
import ExtraControls from './components/ExtraControls';
import QueryEvidence from './components/QueryEvidence';
import RuleSummary from './components/RuleSummary';
import SegmentComparison from './components/SegmentComparison';
import SegmentTrend from './components/SegmentTrend';
import { SEGMENT, SEGMENTS, classificationReason } from './brand';
import { buildBrandCsv } from './exportRows';
import { fetchEvidence, fetchSegmentDaily, fetchSegmentTotals } from './query';
import { comparisonRows, knownTotals } from './rows';
import {
    URL_KEYS,
    hasReportScope,
    initialDraft,
    reportDraftFromParams,
    reportParamsFromScope,
    resolveReportScope,
} from './scope';

const IDLE_SIDE = { status: 'idle', error: null, current: null, baseline: null };
const IDLE_EVIDENCE = { status: 'idle', error: null, rows: [] };

const idleSides = () =>
    SEGMENTS.reduce((sides, segment) => ({ ...sides, [segment]: { ...IDLE_SIDE } }), {});

const loadingSides = () =>
    SEGMENTS.reduce(
        (sides, segment) => ({ ...sides, [segment]: { ...IDLE_SIDE, status: 'loading' } }),
        {},
    );

const DEFAULT_METRIC = 'clicks';

/**
 * Brand vs Non-brand — one property's search traffic split by a rule the user wrote, with the
 * queries behind the split and an explicit denominator.
 *
 * The split is not Google's. Search Console has no brand field and returns no classification, so
 * this report compiles the user's terms into one RE2 expression and asks for that expression
 * twice: once as `includingRegex` for the brand side and once as `excludingRegex` for the
 * non-brand side. Two complementary filters over the same window, which is what keeps the sides
 * disjoint and what keeps the non-brand figure from being a subtraction — brand taken away from
 * the property's total would be non-brand plus every query Google withheld, dressed up as a
 * measurement.
 *
 * That withheld traffic is why the share here is a *known-query* share: brand clicks over brand
 * plus non-brand clicks, never over the property's own total. The denominator is named in the
 * column heading, in the cards, in the coverage block and in the export, because it is the one
 * number on the page a reader will otherwise assume means something broader than it does.
 *
 * Five requests at most per applied scope, grouped so that failures land where they belong: one
 * call per side for the totals, one per side for the daily series — so a side that errors leaves
 * the other's figures standing and only withholds the share that needed both — and one for the
 * evidence table, sent for a single side and a single window because it is the only request here
 * carrying a query dimension.
 *
 * Nothing is persisted. The definition is session-only: it lives in this page's address, which is
 * the whole of its lifetime, so a reload or a shared link reproduces the rule along with the
 * figures and closing the tab leaves nothing behind. A durable brand configuration would be a
 * different feature, with a different place to find and change it.
 */
class BrandNonBrand extends Component {
    constructor(props) {
        super(props);

        this.handleDraftChange = this.handleDraftChange.bind(this);
        this.handleTermsChange = this.handleTermsChange.bind(this);
        this.handleApply = this.handleApply.bind(this);
        this.handleRefresh = this.handleRefresh.bind(this);
        this.handleToggleControls = this.handleToggleControls.bind(this);
        this.handleMetric = this.handleMetric.bind(this);
        this.handleEvidenceSegment = this.handleEvidenceSegment.bind(this);
        this.handleExport = this.handleExport.bind(this);
        this.handlePrint = this.handlePrint.bind(this);
        this.handleDownloadImage = this.handleDownloadImage.bind(this);
        this.reasonFor = this.reasonFor.bind(this);

        this.credentialsDomainId = props.credentials_domain_id || '';
        this.reportRef = createRef();

        // Bumped by every apply and refresh; a response carrying an older value is stale.
        this.generation = 0;
        this.evidenceGeneration = 0;
        this.isMounted_ = false;

        const period = defaultPeriod();

        // The address is read once, here, so the controls show the report it names from the first
        // paint rather than flicking from the defaults to it a moment later.
        const params = readParams();
        this.urlDraft = reportDraftFromParams(params);
        this.urlHasScope = hasReportScope(params);

        this.state = {
            properties: [],
            loadingProperties: true,
            propertiesError: null,
            latestDate: '',

            draft: { ...initialDraft(period), ...this.urlDraft },

            applied: null,
            loading: false,
            fetchedAt: null,
            controlsOpen: true,
            capturing: false,

            totals: idleSides(),
            daily: idleSides(),

            metric: DEFAULT_METRIC,

            evidenceSegment: SEGMENT.BRAND,
            evidence: { ...IDLE_EVIDENCE },
        };
    }

    async componentDidMount() {
        this.isMounted_ = true;

        let properties = [];
        let propertiesError = null;

        try {
            const sites = await getUserDomains();
            properties = sites.filter((site) => ELIGIBLE_PERMISSIONS.has(site.permissionLevel));
        } catch (err) {
            console.log(err);
            propertiesError = err?.message ?? this.props.t('global.something_went_wrong');
        }

        if (!this.isMounted_) return;

        // A link naming a property this account cannot read is not quietly answered with another
        // property's figures: the first one is selected and the scope bar is left open for the
        // user to confirm, rather than a report being run under a scope nobody asked for.
        const requested = this.urlDraft.siteUrl
            ? properties.find((item) => item.siteUrl === this.urlDraft.siteUrl)
            : null;
        const property = requested ?? properties[0];
        const autoApply = this.urlHasScope && !!property && (!this.urlDraft.siteUrl || !!requested);

        this.setState(
            (state) => ({
                properties,
                propertiesError,
                loadingProperties: false,
                draft: property
                    ? {
                          ...state.draft,
                          siteUrl: property.siteUrl,
                          permissionLevel: property.permissionLevel,
                      }
                    : state.draft,
            }),
            () => {
                // Dates that came from the address are the user's own: the probe still runs, for
                // the preset anchor, but it does not move them.
                if (property) {
                    this.suggestDates(property.siteUrl, { seedDates: !this.urlDraft.startDate });
                }
                if (autoApply) this.handleApply();
            },
        );
    }

    componentWillUnmount() {
        this.isMounted_ = false;
    }

    /**
     * Seeds the date controls from the property's most recent activity, so the window the user is
     * offered has something in it rather than ending in days Search Console has not filled yet.
     */
    async suggestDates(siteUrl, { seedDates = true } = {}) {
        if (!this.credentialsDomainId || !siteUrl) return;

        try {
            const latest = await probeLatestDate({
                credentialsDomainId: this.credentialsDomainId,
                siteUrl,
                searchType: this.state.draft.searchType,
            });

            if (!latest || !this.isMounted_) return;

            this.setState((state) => {
                if (state.draft.siteUrl !== siteUrl) return null;
                // Kept as well as applied: the date presets count back from this day, so one
                // click lands on days the property has data for.
                if (!seedDates) return { latestDate: latest };

                return { latestDate: latest, draft: { ...state.draft, ...periodEndingOn(latest) } };
            });
        } catch (err) {
            // A probe that fails costs nothing: the controls keep their default window.
            console.log(err);
        }
    }

    scope() {
        return resolveReportScope(this.state.draft, this.props.t);
    }

    /** The metric the trend draws, held to the ones this surface actually reports. */
    metric() {
        const searchType = this.state.applied?.searchType ?? this.state.draft.searchType;
        return metricsFor(searchType).includes(this.state.metric)
            ? this.state.metric
            : DEFAULT_METRIC;
    }

    /**
     * The window the evidence table covers.
     *
     * It is the only request carrying a query dimension, which multiplies the rows retrieved per
     * day, so it is held to the shared detail bound rather than holding the whole report to it.
     * When that shortens the window the block says so — a block covering fewer days than the ones
     * above it must not look like it disagrees with them.
     */
    evidenceWindow(scope) {
        return detailPeriod(scope.current, DETAIL_MAX_DAYS);
    }

    handleDraftChange(patch) {
        this.setState(
            (state) => ({ draft: { ...state.draft, ...patch } }),
            () => {
                if (patch.siteUrl) {
                    const property = this.state.properties.find(
                        (item) => item.siteUrl === patch.siteUrl,
                    );
                    this.setState(
                        (state) => ({
                            draft: {
                                ...state.draft,
                                permissionLevel: property?.permissionLevel ?? '',
                            },
                        }),
                        () => this.suggestDates(patch.siteUrl),
                    );
                }
            },
        );
    }

    handleTermsChange(terms) {
        this.setState((state) => ({ draft: { ...state.draft, terms } }));
    }

    handleToggleControls() {
        this.setState((state) => ({ controlsOpen: !state.controlsOpen }));
    }

    handleApply() {
        const scope = this.scope();
        if (scope.errors.length) return;
        this.load(scope);
    }

    handleRefresh() {
        if (this.state.applied) this.load(this.state.applied);
    }

    /**
     * Runs the applied scope: both sides' totals, both sides' daily series and the evidence for
     * whichever side is open, all started at once and each landing on its own.
     *
     * The open evidence side is kept across an apply. It is a side of the rule rather than a row
     * of returned data, so it still exists under the new scope; what it holds is simply refetched.
     */
    load(scope) {
        this.generation += 1;
        this.evidenceGeneration += 1;
        const generation = this.generation;

        // Written here rather than in handleApply so the address also follows a refresh, and so
        // it only ever describes a scope that was actually run.
        writeParams(reportParamsFromScope(scope), URL_KEYS);

        this.setState({
            applied: scope,
            controlsOpen: false,
            loading: true,
            fetchedAt: new Date().toISOString(),

            totals: loadingSides(),
            daily: loadingSides(),
            evidence: { ...IDLE_EVIDENCE, status: 'loading' },
        });

        for (const segment of SEGMENTS) {
            this.loadTotals(scope, segment, generation);
            this.loadDaily(scope, segment, generation);
        }

        this.loadEvidence(scope, this.state.evidenceSegment);
    }

    /** Records one side's outcome without disturbing the other's. */
    setSide(key, segment, value) {
        this.setState((state) => ({ [key]: { ...state[key], [segment]: value } }));
    }

    /**
     * Block 02 — one side's dimensionless totals for each window.
     *
     * `loading` is cleared as soon as the totals land, because they are the first figures on the
     * page; the daily series and the evidence carry their own states in their own blocks rather
     * than holding the scope bar's spinner for everything at once.
     */
    async loadTotals(scope, segment, generation) {
        const hasPosition = reportsPosition(scope.searchType);

        try {
            const { current, baseline } = await fetchSegmentTotals({
                credentialsDomainId: this.credentialsDomainId,
                scope,
                segment,
            });

            if (!this.isMounted_ || generation !== this.generation) return;

            this.setState({ loading: false });
            this.setSide('totals', segment, {
                status: 'done',
                error: null,
                current: normalizeTotals(current, hasPosition),
                baseline: scope.baseline ? normalizeTotals(baseline, hasPosition) : null,
            });
        } catch (err) {
            console.log('totals', segment, err);
            if (!this.isMounted_ || generation !== this.generation) return;

            this.setState({ loading: false });
            this.setSide('totals', segment, {
                ...IDLE_SIDE,
                status: 'error',
                error: err?.message ?? this.props.t('global.something_went_wrong'),
            });
        }
    }

    /**
     * Block 03 — one side's daily rows for each window, laid on the period's own calendar.
     *
     * The raw rows are kept beside the built series: the known-query totals above the chart are
     * summed from both sides' rows together, which has to happen on the responses rather than on
     * the padded series, where a day neither side returned is a null and not a row.
     */
    async loadDaily(scope, segment, generation) {
        const hasPosition = reportsPosition(scope.searchType);

        const read = (rows, period) =>
            period
                ? {
                      rows,
                      series: buildSeries(rows, period, hasPosition),
                      coverage: coverageFor(rows, period),
                      totals: sumRows(rows, hasPosition),
                  }
                : null;

        try {
            const { current, baseline } = await fetchSegmentDaily({
                credentialsDomainId: this.credentialsDomainId,
                scope,
                segment,
            });

            if (!this.isMounted_ || generation !== this.generation) return;

            this.setSide('daily', segment, {
                status: 'done',
                error: null,
                current: read(current, scope.current),
                baseline: read(baseline, scope.baseline),
            });
        } catch (err) {
            console.log('daily', segment, err);
            if (!this.isMounted_ || generation !== this.generation) return;

            this.setSide('daily', segment, {
                ...IDLE_SIDE,
                status: 'error',
                error: err?.message ?? this.props.t('global.something_went_wrong'),
            });
        }
    }

    handleMetric(metric) {
        this.setState({ metric });
    }

    handleEvidenceSegment(segment) {
        const scope = this.state.applied;
        this.setState({ evidenceSegment: segment });
        if (scope) this.loadEvidence(scope, segment);
    }

    /** Block 04 — the queries on one side, for the detail window. */
    async loadEvidence(scope, segment) {
        this.evidenceGeneration += 1;
        const evidenceGeneration = this.evidenceGeneration;

        this.setState({ evidence: { ...IDLE_EVIDENCE, status: 'loading' } });

        const hasPosition = reportsPosition(scope.searchType);
        const { period } = this.evidenceWindow(scope);

        try {
            const rows = await fetchEvidence({
                credentialsDomainId: this.credentialsDomainId,
                scope,
                segment,
                period,
            });

            if (!this.isMounted_ || evidenceGeneration !== this.evidenceGeneration) return;

            this.setState({
                evidence: {
                    status: 'done',
                    error: null,
                    rows: rows.map((row) => ({
                        id: row?.keys?.[0] ?? '',
                        key: row?.keys?.[0] ?? '',
                        segment,
                        ...rowMetrics(row, hasPosition),
                    })),
                },
            });
        } catch (err) {
            console.log('evidence', err);
            if (!this.isMounted_ || evidenceGeneration !== this.evidenceGeneration) return;

            this.setState({
                evidence: {
                    ...IDLE_EVIDENCE,
                    status: 'error',
                    error: err?.message ?? this.props.t('global.something_went_wrong'),
                },
            });
        }
    }

    /** Why one evidence row is on the side Search Console returned it on. */
    reasonFor(item) {
        return classificationReason(this.props.t, this.state.applied, item.key, item.segment);
    }

    exportName(prefix) {
        const scope = this.state.applied;
        return `${prefix}-${scope?.siteUrl ?? 'property'}-${scope?.current?.startDate ?? ''}`
            .replace(/[^a-z0-9_.-]+/gi, '-')
            .toLowerCase();
    }

    handleExport() {
        const scope = this.state.applied;
        if (!scope) return;

        downloadCsv(
            buildBrandCsv({
                t: this.props.t,
                scope,
                rows: comparisonRows(scope, this.state.totals),
                fetchedAt: this.state.fetchedAt
                    ? moment(this.state.fetchedAt).format('YYYY-MM-DD HH:mm')
                    : null,
            }),
            `${this.exportName('brand-non-brand')}.csv`,
        );
    }

    handlePrint() {
        window.print();
    }

    /**
     * Captures the loaded report as an image, from the DOM already on screen — no further request
     * and no upload. The controls that produced the report and the export actions themselves are
     * left out of the frame, while the applied rule and the coverage labels stay in it, so the
     * picture still says what it is a picture of and under which definition.
     */
    async handleDownloadImage() {
        const container = this.reportRef.current;
        if (!container) return;

        this.setState({ capturing: true });

        try {
            const canvas = await html2canvas(container, {
                backgroundColor: '#ffffff',
                scale: 2,
                ignoreElements: (element) =>
                    element.classList?.contains('gsc-tool-controls') ||
                    element.classList?.contains('gsc-tool-controls-bottom') ||
                    element.classList?.contains('gsc-tool-page-actions') ||
                    // The shared chart and table carry their own refresh and download buttons.
                    element.classList?.contains('chart-action-btns') ||
                    element.classList?.contains('dt-actions__bar'),
            });

            const link = document.createElement('a');
            link.href = canvas.toDataURL('image/png');
            link.download = `${this.exportName('brand-non-brand')}.png`;
            link.click();
        } catch (err) {
            console.log(err);
        } finally {
            if (this.isMounted_) this.setState({ capturing: false });
        }
    }

    renderReport(applied) {
        const { t } = this.props;

        const rows = comparisonRows(applied, this.state.totals);
        const known = knownTotals(applied, this.state.daily);
        const settled = SEGMENTS.some((segment) => this.state.totals[segment].status === 'done');
        const evidence = this.evidenceWindow(applied);

        // The strip reports the window's coverage, which is both sides together: a date is
        // covered when either side returned it. So it waits for both, and only calls the window
        // unreadable when neither side came back — one side failing leaves a coverage figure that
        // is real, just narrower than it would have been.
        const daily = this.state.daily;
        const dailyStatus = SEGMENTS.some((segment) =>
            ['idle', 'loading'].includes(daily[segment].status),
        )
            ? 'loading'
            : SEGMENTS.every((segment) => daily[segment].status === 'error')
            ? 'error'
            : 'done';
        const dailyError = SEGMENTS.map((segment) => daily[segment].error).find(Boolean) ?? null;

        return (
            <>
                <CoverageStrip
                    scope={applied}
                    current={{
                        status: dailyStatus,
                        coverage: known.current?.coverage ?? null,
                        error: dailyError,
                    }}
                    baseline={
                        applied.baseline
                            ? {
                                  status: dailyStatus,
                                  coverage: known.baseline?.coverage ?? null,
                                  error: dailyError,
                              }
                            : null
                    }
                    fetchedAt={this.state.fetchedAt}
                />

                <RuleSummary scope={applied} />

                <SegmentComparison
                    scope={applied}
                    sides={this.state.totals}
                    rows={rows}
                    exportable={settled}
                    onExport={this.handleExport}
                />

                <SegmentTrend
                    scope={applied}
                    sides={this.state.daily}
                    known={known}
                    metric={this.metric()}
                    onMetric={this.handleMetric}
                />

                <QueryEvidence
                    scope={applied}
                    segment={this.state.evidenceSegment}
                    onSegment={this.handleEvidenceSegment}
                    status={this.state.evidence.status}
                    error={this.state.evidence.error}
                    rows={this.state.evidence.rows}
                    period={evidence.period}
                    shortened={evidence.shortened}
                    reasonFor={this.reasonFor}
                />

                <BrandCoverage
                    scope={applied}
                    evidencePeriod={evidence.period}
                    exportable={settled}
                    onExport={this.handleExport}
                />

                <p className="gsc-tool-table-note">
                    {t('tools.brand_non_brand.footnote', {
                        period: formatPeriod(applied.current),
                    })}
                </p>
            </>
        );
    }

    render() {
        const { t } = this.props;
        const scope = this.scope();
        const applied = this.state.applied;
        const ready = SEGMENTS.some((segment) => this.state.totals[segment].status === 'done');

        return (
            <div className="gsc-tool" ref={this.reportRef}>
                <div className="gsc-tool-title-row">
                    <div>
                        <h2 className="page-title">{t('tools.brand_non_brand.header')}</h2>
                        <p className="gsc-tool-sub">{t('tools.brand_non_brand.subtitle')}</p>
                    </div>

                    <div className="gsc-tool-page-actions">
                        <button
                            type="button"
                            className="button button-outline button-primary btn"
                            onClick={this.handleDownloadImage}
                            disabled={!ready || this.state.capturing}
                        >
                            <i className="icon-fi_download" />
                            {this.state.capturing
                                ? t('tools.gsc.exports.capturing')
                                : t('tools.gsc.exports.image')}
                        </button>
                        <button
                            type="button"
                            className="button button-outline button-primary btn"
                            onClick={this.handlePrint}
                            disabled={!ready}
                        >
                            <i className="icon-fi_download" />
                            {t('tools.gsc.exports.full_page')}
                        </button>
                    </div>
                </div>

                {!this.credentialsDomainId && (
                    <div className="gsc-tool-banner error">
                        {t('tools.gsc.controls.no_credentials')}
                    </div>
                )}

                <ScopeControls
                    draft={this.state.draft}
                    scope={scope}
                    applied={applied}
                    open={this.state.controlsOpen}
                    properties={this.state.properties}
                    loadingProperties={this.state.loadingProperties}
                    propertiesError={this.state.propertiesError}
                    latestDate={this.state.latestDate}
                    loading={this.state.loading}
                    onChange={this.handleDraftChange}
                    onApply={this.handleApply}
                    onRefresh={this.handleRefresh}
                    onToggle={this.handleToggleControls}
                    extraScopeLabels={[t(`tools.brand_non_brand.policies.${scope.policy}`)]}
                    footer={
                        <BrandTerms
                            policy={this.state.draft.policy}
                            terms={this.state.draft.terms}
                            pattern={this.state.draft.pattern}
                            onChange={this.handleTermsChange}
                        />
                    }
                >
                    <ExtraControls draft={this.state.draft} onChange={this.handleDraftChange} />
                </ScopeControls>

                {!applied ? (
                    <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
                        <div className="card-body gsc-tool-load-state">
                            <span className="gsc-tool-load-icon" aria-hidden="true">
                                <i className="icon-scale-1" />
                            </span>
                            <h4>{t('tools.brand_non_brand.idle_title')}</h4>
                            <p>{t('tools.brand_non_brand.idle')}</p>
                        </div>
                    </div>
                ) : (
                    this.renderReport(applied)
                )}
            </div>
        );
    }
}

export default withTranslation()(BrandNonBrand);
